import { createFileRoute } from '@tanstack/react-router';
import { TicketOfferForm } from '@/components/Offering/TicketOfferForm';
import { DataNotFound } from '@/components/DataNotFound';
import { cn } from '@/helpers/classnames';
import { Loading } from '@/components/Loading';
import { useOfferingGetBySlug } from '@/api/reseller';
import { CompleteOffering, OFFERINGS } from '@/types/offering';
import { FormattedMessage } from 'react-intl';
import { DocumentHead } from '@/components/core/DocumentHead';
import { useFavicon } from '@/hooks/useFavicon';

export const Route = createFileRoute('/offerings/$offerId')({
  component: OfferPage,
});

function OfferPage() {
  const { offerId } = Route.useParams();

  const { data: offering, isLoading } = useOfferingGetBySlug(offerId);

  const tenantId = OFFERINGS[offerId]?.tenantId;

  useFavicon(tenantId || '');

  if (isLoading) {
    return <Loading />;
  }

  if (!offering) {
    return (
      <>
        <DocumentHead
          title="Erbjudande hittades inte"
          description="Det begärda erbjudandet kunde inte hittas"
          appendAppName={false}
        />
        <DataNotFound
          description={
            <FormattedMessage defaultMessage="Erbjudandet hittades inte" />
          }
        />
      </>
    );
  }

  if (offering.offers.length === 0) {
    return (
      <>
        <DocumentHead
          title={`${offering.name} - Ogiltigt erbjudande`}
          description="Erbjudandet är inte giltigt eller har blivit felkonfigurerat"
          appendAppName={false}
        />
        <DataNotFound
          description={
            <FormattedMessage defaultMessage="Erbjudandet är inte giltigt eller har blivit felkonfigurerat" />
          }
        />
      </>
    );
  }

  const fullOffering: CompleteOffering = {
    ...offering,
    ...OFFERINGS[offerId],
  };

  return (
    <div
      className="flex h-fit min-h-screen w-screen flex-col items-center bg-background-50 py-10"
      style={{ backgroundColor: fullOffering.bgColor }}
    >
      <DocumentHead
        title={`${offering.name} - Specialerbjudande`}
        description={`Specialerbjudande för ${offering.name}`}
        appendAppName={false}
      />
      <div
        className={cn(
          'flex w-full max-w-[620px] p-4',
          fullOffering.organization_id,
        )}
      >
        <TicketOfferForm offering={fullOffering} />
      </div>
    </div>
  );
}
